import React from 'react';
import { TaskStatus, PriorityLevel, ProjectStatus, UserRole } from '../../../shared/types.ts';

interface StatusBadgeProps {
  status: TaskStatus | ProjectStatus;
  size?: 'sm' | 'md';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs';

  switch (status) {
    case 'done':
    case 'completed':
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 ${sizeClasses}`}>
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-emerald-500"></span>
          Done
        </span>
      );
    case 'in_progress':
    case 'active':
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20 ${sizeClasses}`}>
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-brand-500 animate-pulse"></span>
          In Progress
        </span>
      );
    case 'in_review':
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 ${sizeClasses}`}>
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-purple-500"></span>
          In Review
        </span>
      );
    case 'todo':
    case 'planning':
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 ${sizeClasses}`}>
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-amber-500"></span>
          To Do
        </span>
      );
    case 'backlog':
    case 'on_hold':
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-surface-500/10 text-surface-600 dark:text-surface-400 border border-surface-500/20 ${sizeClasses}`}>
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-surface-400"></span>
          Backlog
        </span>
      );
    case 'cancelled':
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 ${sizeClasses}`}>
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-rose-500"></span>
          Cancelled
        </span>
      );
    default:
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-surface-200 dark:bg-surface-800 text-surface-700 dark:text-surface-300 ${sizeClasses}`}>
          {status}
        </span>
      );
  }
};

interface PriorityBadgeProps {
  priority: PriorityLevel;
  size?: 'sm' | 'md';
}

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({ priority, size = 'md' }) => {
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs';

  switch (priority) {
    case 'urgent':
      return (
        <span className={`inline-flex items-center font-bold rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 ${sizeClasses}`}>
          ⚡ Urgent
        </span>
      );
    case 'high':
      return (
        <span className={`inline-flex items-center font-bold rounded-full bg-[#121417] text-white dark:bg-white dark:text-black ${sizeClasses}`}>
          ▲ High
        </span>
      );
    case 'medium':
      return (
        <span className={`inline-flex items-center font-semibold rounded-full bg-brand-500/15 text-brand-700 dark:text-brand-300 border border-brand-500/30 ${sizeClasses}`}>
          ● Medium
        </span>
      );
    case 'low':
      return (
        <span className={`inline-flex items-center font-medium rounded-full bg-surface-200 dark:bg-surface-800 text-surface-600 dark:text-surface-300 ${sizeClasses}`}>
          ▼ Low
        </span>
      );
    default:
      return <span>{priority}</span>;
  }
};

interface RoleBadgeProps {
  role: UserRole;
}

export const RoleBadge: React.FC<RoleBadgeProps> = ({ role }) => {
  switch (role) {
    case 'admin':
      return (
        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20">
          Admin
        </span>
      );
    case 'manager':
      return (
        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
          Manager
        </span>
      );
    case 'team_lead':
      return (
        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20">
          Team Lead
        </span>
      );
    case 'sqa':
      return (
        <span
          className="px-2 py-0.5 text-xs font-semibold rounded bg-teal-500/10 text-teal-600 dark:text-teal-400 border border-teal-500/20"
        >
          SQA
        </span>
      );
    case 'guest':
      return (
        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
          Guest
        </span>
      );
    case 'viewer':
      return (
        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20">
          Viewer
        </span>
      );
    default:
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-surface-200 dark:bg-surface-800 text-surface-600 dark:text-surface-300">
          Member
        </span>
      );
  }
};
